import React from 'react';
import { Button, OutlineButton, Modal, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

/**
 * AdjacentSeatDisclaimerDialog component.
 * Shows a warning modal when a user with adjacent seats in their original purchase
 * has the adjacent seat toggle turned off.
 *
 * @param {object} props
 * @param {boolean} props.show - Should show or hide the dialog
 * @param {function} props.onAccept - Callback when user accepts to continue without adjacent seats
 * @param {function} props.onCancel - Callback when user wants to keep adjacent seats
 * @returns AdjacentSeatDisclaimerDialog component
 */
const AdjacentSeatDisclaimerDialog = ({ show, onAccept, onCancel }) => {
  const { t } = useTranslation('seats');

  /**
   * Handle accept action - user wants to continue without adjacent seats
   */
  const handleAccept = () => {
    if (onAccept && typeof onAccept === 'function') {
      onAccept();
    }
  };

  /**
   * Handle cancel action - user wants to keep adjacent seats
   */
  const handleCancel = () => {
    if (onCancel && typeof onCancel === 'function') {
      onCancel();
    }
  };

  if (!show) return null;

  return (
    <Modal
      responsiveSize="S"
      title={t('adjacent_disclaimer_dialog.title')}
      titleColor="primary"
      defaultOpened
      onCloseModal={handleCancel}
      rounded="M"
      footer={
        <div className="flex w-full justify-between gap-2 mb-3">
          <OutlineButton
            text={t('adjacent_disclaimer_dialog.cancel')}
            onClick={handleCancel}
            variant="primary"
            fullWidth
            fontSize="S"
            padding="S"
          />
          <Button
            text={t('adjacent_disclaimer_dialog.accept')}
            variant="primary"
            onClick={handleAccept}
            fullWidth
            fontSize="S"
            padding="S"
          />
        </div>
      }
    >
      <Text mobileSize="S">{t('adjacent_disclaimer_dialog.message')}</Text>
    </Modal>
  );
};

AdjacentSeatDisclaimerDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AdjacentSeatDisclaimerDialog;
